'use client';

import React from 'react';
import styled from 'styled-components';
import { ActivityLog } from '@/types/incident';
import { i18n } from '@/config/i18n';

/**
 * ActivityLogTable Component
 * 
 * Displays a table of activity log entries for an incident
 * Used in the incident detail page to show the timeline of events
 */

export interface ActivityLogTableProps {
  /** Array of activity log entries */
  activities: ActivityLog[];
  /** Optional className for custom styling */
  className?: string;
}

// ============================================================================
// Styled Components
// ============================================================================

const TableContainer = styled.div`
  width: 100%;
  overflow-x: auto;
  background: #f5f5f5;
  border-radius: 8px;
  padding: 0;
`;

const Table = styled.table`
  width: 100%;
  border-collapse: separate;
  border-spacing: 0;
  background: #f5f5f5;
`;

const TableHead = styled.thead`
  background: transparent;
`;

const TableHeadRow = styled.tr`
  border-bottom: 1px solid #e5e7eb;
`;

const TableHeadCell = styled.th`
  text-align: left;
  padding: 12px 16px;
  font-size: 14px;
  font-weight: 600;
  color: #111827;
  background: transparent;
  
  &:first-child {
    padding-left: 16px;
  }
  
  &:last-child {
    padding-right: 16px;
    text-align: right;
  }
`;

const TableBody = styled.tbody`
  background: #f5f5f5;
`;

const TableRow = styled.tr`
  background: #ffffff;
  border-bottom: 1px solid #e5e7eb;
  
  &:last-child {
    border-bottom: none;
  }
`;

const TableCell = styled.td`
  padding: 16px;
  font-size: 14px;
  font-weight: 400;
  color: #111827;
  background: #ffffff;
  
  &:first-child {
    padding-left: 16px;
  }
  
  &:last-child {
    padding-right: 16px;
    text-align: right;
    color: #111827;
  }
`;

const EmptyState = styled.div`
  padding: 32px;
  text-align: center;
  color: ${({ theme }) => theme.colors.text.secondary};
  font-size: 14px;
`;

// ============================================================================
// Component
// ============================================================================

export const ActivityLogTable: React.FC<ActivityLogTableProps> = ({
  activities,
  className,
}) => {
  const formatDate = (dateString: string): string => {
    const date = new Date(dateString);
    return date.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
    });
  };

  if (!activities || activities?.length === 0) {
    return (
      <EmptyState>
        {i18n.incidents.detail.activityLog.noActivity}
      </EmptyState>
    );
  }

  return (
    <TableContainer className={className}>
      <Table>
        <TableHead>
          <TableHeadRow>
            <TableHeadCell>{i18n.incidents.detail.activityLog.rootCause}</TableHeadCell>
            <TableHeadCell>{i18n.incidents.detail.activityLog.time}</TableHeadCell>
          </TableHeadRow>
        </TableHead>
        <TableBody>
          {activities.map((activity) => (
            <TableRow key={activity.id}>
              <TableCell>{activity.description}</TableCell>
              <TableCell>{formatDate(activity.created_at)}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

export default ActivityLogTable;

